import { Header } from '@/components/header'
import { Footer } from '@/components/footer'
import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'

export default function HiringCalgaryDeckContractorBlog() {
  // Handle client-side navigation
  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Only intercept internal links
    if (href.startsWith('/') || href.includes('busybeaverconstruction.com/')) {
      e.preventDefault()
      const path = href.replace(/^https?:\/\/[^\/]+/, '')
      window.history.pushState({}, '', path)
      window.dispatchEvent(new Event('popstate'))
      window.scrollTo(0, 0)
    }
  }

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Consolidated Schema Data for Hiring Guide Blog
  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Questions to Ask Before Hiring a Calgary Deck Contractor",
        "description": "Hiring a deck contractor in Calgary is about more than comparing prices. Before you sign, ask about permits, foundations, materials, timelines, payments, and warranties.",
        "author": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "url": "https://busybeaverconstruction.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Busy Beaver Construction",
          "logo": {
            "@type": "ImageObject",
            "url": "https://busybeaverconstruction.com/assets/images/logo.png"
          }
        },
        "datePublished": "2026-08-28T00:00:00+00:00",
        "dateModified": "2026-08-28T00:00:00+00:00",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": "https://busybeaverconstruction.com/blog/questions-to-ask-hiring-calgary-deck-contractor"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://busybeaverconstruction.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://busybeaverconstruction.com/blog"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "Questions to Ask Before Hiring a Calgary Deck Contractor",
            "item": "https://busybeaverconstruction.com/blog/questions-to-ask-hiring-calgary-deck-contractor"
          }
        ]
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What should I ask a contractor before hiring them?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ask about their Calgary experience, insurance and WCB coverage, permit requirements, project scope, materials, timeline, payment schedule, warranty, and references. The goal is to understand who is responsible for the work, what is included in the price, and how potential problems will be handled."
            }
          },
          {
            "@type": "Question",
            "name": "How do I choose a good contractor in Calgary?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Look beyond price. Compare relevant Calgary experience, documentation, scope clarity, communication, workmanship, project supervision, and accountability. A contractor should be able to explain how they will approach your specific property and project."
            }
          },
          {
            "@type": "Question",
            "name": "Does a Calgary deck require a permit?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "A deck more than 0.6 metres (2 feet) above grade requires a building permit in Calgary. Other project details can affect the requirements, so confirm the applicable permits with the City of Calgary for your specific project. For more information, see our guide to whether you need a permit to build a deck in Calgary."
            }
          },
          {
            "@type": "Question",
            "name": "Should the contractor handle the building permit?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Ask the contractor exactly what they will handle, including drawings, permit applications, and inspections. Even when the contractor manages the process, homeowners should understand which permits their project requires and what is included in the contract."
            }
          },
          {
            "@type": "Question",
            "name": "How many contractor quotes should I get?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Getting multiple quotes can help you understand the market, but there is no useful number if the quotes are not comparable. Focus on contractors providing an equivalent scope of work, including materials, foundation, permits, labour, cleanup, and warranty."
            }
          },
          {
            "@type": "Question",
            "name": "What are the biggest contractor red flags?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Watch for contractors who dismiss required permits, cannot explain Calgary requirements, provide vague quotes, demand large upfront payments, avoid insurance or WCB questions, have unclear supervision, pressure you to sign, or cannot explain change orders and warranty coverage."
            }
          }
        ]
      }
    ]
  }

  return (
    <main className="bg-gray-50">
      <Helmet>
        <title>Questions to Ask Before Hiring a Calgary Deck Contractor | Busy Beaver Construction</title>
        <meta name="description" content="Hiring a deck contractor in Calgary is about more than comparing prices. Before you sign, ask about permits, foundations, materials, timelines, payments, and warranties." />
        <meta name="keywords" content="Calgary deck contractor, hiring a contractor Calgary, deck builder Calgary, fence installation Calgary, contractor quotes" />
        <link rel="canonical" href="https://busybeaverconstruction.com/blog/questions-to-ask-hiring-calgary-deck-contractor" />
        <meta property="og:title" content="Questions to Ask Before Hiring a Calgary Deck Contractor" />
        <meta property="og:description" content="Hiring a deck contractor in Calgary is about more than comparing prices. Before you sign, ask about permits, foundations, materials, timelines, payments, and warranties." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://busybeaverconstruction.com/blog/questions-to-ask-hiring-calgary-deck-contractor" />
        <meta property="og:image" content="/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      
      {/* JSON-LD Schema placed OUTSIDE Helmet to ensure rendering */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-[450px] md:h-[500px] flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60 z-10"></div>
        <img 
          src="/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg"
          alt="Calgary deck contractor hiring guide"
          className="absolute inset-0 w-full h-full object-cover loading-eager"
          fetchPriority="high"
        />
        <div className="relative z-20 mt-24 text-center max-w-5xl mx-auto px-4">
          <div className="inline-block bg-[#86efac] rounded py-1 px-6 mb-6">
            <p className="text-black text-sm font-bold tracking-wider uppercase">Hiring Guide</p>
          </div>
          <h1 className="text-white text-4xl md:text-5xl lg:text-7xl font-primary drop-shadow-lg mb-4 leading-tight italic">
            Questions to Ask Before Hiring a Calgary Deck Contractor
          </h1>
        </div>
      </section>
      
      {/* Blog Content Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 relative">
            
            <article className="lg:col-span-8 space-y-6 text-[17px] text-gray-700 leading-relaxed font-text">
              
              <div className="article-introduction">
                <p className="mb-6">
                  Hiring a deck contractor in Calgary is about more than comparing prices. Before you sign, ask about permits, foundations, materials, timelines, payments, and warranties.
                </p>
                <p className="mb-6">
                  The right questions can help you compare contractors based on experience, scope, workmanship, and accountability, not just the lowest quote.
                </p>
              </div>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">1. How Much Calgary Experience Do You Have With Projects Like Mine?
              </h2>
              <p className="mb-4">
                Start by asking how long the contractor has been working in Calgary, Alberta, and how many projects similar to yours they have completed. Experience matters more when it is relevant to your project, whether you are planning a new deck, fence, pergola, or outdoor living space.
              </p>
              <p className="mb-4">
                Ask to see recent examples from Calgary and, where possible, speak with previous customers. You can also ask who will supervise the project once construction begins. The person providing your estimate may not be the person managing the crew on site.
              </p>
              <p className="mb-4">
                A contractor familiar with Calgary neighbourhoods and local site conditions should be able to explain how they approach your specific property, materials, access, and construction requirements.
              </p>
              <p className="mb-6">
                <strong>Ask:</strong> &quot;Who will be responsible for supervising my project from start to finish?&quot;
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-4">
                Local experience gives you a better way to judge whether a Calgary contractor understands the type of work, property conditions, and project challenges you may encounter. It also helps you know who is accountable once construction begins.
              </p>
              <p className="mb-6">
                When comparing contractors, look for relevant Calgary experience, recent similar projects, clear references, and direct project supervision, not simply the number of years a company has been in business.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">2. Does My Project Require a City of Calgary Permit?</h2>
              <p className="mb-4">
                Before hiring a contractor, ask exactly which permits your project requires and who will handle them. In Calgary, a deck with a surface more than 0.6 metres (2 feet) above grade requires a building permit. Covered structures and certain structural changes can involve additional requirements.
              </p>
              <p className="mb-4">Ask the contractor:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Which permits does my project need?</li>
                <li>Who prepares the drawings?</li>
                <li>Who submits the permit application?</li>
                <li>Who arranges required inspections?</li>
                <li>Will permit and inspection costs be included in the quote?</li>
                <li>What happens if the City requires changes to the plans?</li>
              </ul>
              <p className="mb-4">
                You should also ask how the contractor will confirm setbacks and other site requirements before construction begins. Do not rely on a verbal statement that a permit is unnecessary.
              </p>
              <p className="mb-6">
                For more detail on Calgary deck permit requirements, see our guide to <a href="https://busybeaverconstruction.com/blog/4" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/4')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">whether you need a permit to build a deck in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Question Matters</h3>
              <p className="mb-4">
                Getting the permit requirements right can help you avoid problems later. Work that does not meet Calgary's requirements may lead to delays, compliance issues, or complaints from neighbours. It can also create complications when you sell your home, particularly if an unpermitted structure or setback issue is identified during a Real Property Report (RPR) review.
              </p>
              <p className="mb-6">
                A contractor should be willing to explain the applicable requirements and how permits, drawings, and inspections will be handled.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">3. How Will You Handle Calgary's Soil, Frost and Weather Conditions?</h2>
              <p className="mb-4">
                Calgary's weather can be hard on outdoor structures. Before hiring a contractor, ask how the foundation, framing, drainage, and materials will be suited to local conditions. Freeze and thaw cycles, frost, moisture, snow, and strong summer UV exposure can all affect how a deck performs over time.
              </p>
              <p className="mb-6">
                Ask what foundation system they recommend, such as screw or helical piles or concrete foundations, and why it is appropriate for your property. The contractor should also be able to explain how they will address frost movement, drainage and slope, as well as ledger flashing where a deck connects to the house.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Follow-up Questions</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>What foundation system are you proposing?</li>
                <li>Why is it appropriate for this site?</li>
                <li>How will you account for frost conditions?</li>
                <li>How will water drain away from the structure?</li>
                <li>How will the deck connection be protected from moisture?</li>
              </ul>
              <p className="mb-6">
                For more information about how Calgary weather can affect outdoor construction schedules, see our guide to the <a href="https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">best time to build a deck in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A contractor should be able to explain why a particular foundation and construction approach suits your property, rather than offering the same solution for every home. Clear answers show that the contractor has considered the site and Calgary's conditions before construction begins.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">4. Which Materials Do You Recommend and Why?</h2>
              <p className="mb-4">
                The right decking material depends on your budget, how much maintenance you are willing to do, and how you plan to use the space. In Calgary, you should also consider winter moisture, frost, temperature changes, and strong summer sun.
              </p>
              <p className="mb-6">
                Common options include pressure treated wood, composite decking, hardwood, and vinyl membranes where they suit the project. Each has different requirements for maintenance, appearance, durability, and installation. A contractor should also follow the manufacturer's installation requirements, particularly for composite products.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Don't Ask Only &quot;What's the Best Material?&quot;</h3>
              <p className="mb-2">Instead, ask:</p>
              <p className="mb-4 italic">
                &quot;Which material is best for my project, budget, maintenance expectations and Calgary conditions, and why?&quot;
              </p>
              <p className="mb-4">
                There is no single best decking material for every Calgary home. A good contractor should explain the tradeoffs and recommend an option based on your property and priorities, rather than simply selling the material with the highest price.
              </p>
              <p className="mb-6">
                If you are comparing materials, see our guide to the <a href="https://busybeaverconstruction.com/blog/best-decking-material-calgary-winters" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/best-decking-material-calgary-winters')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">best decking material for Calgary winters</a>. You can also compare two popular composite options in our guide to <a href="https://busybeaverconstruction.com/blog/timbertech-vs-trex-calgary" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/timbertech-vs-trex-calgary')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">TimberTech vs. Trex in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A material recommendation should be based on your specific project, not a one size fits all sales pitch. Asking why a contractor recommends a particular product gives you a clearer picture of its expected maintenance, performance, cost, and suitability for Calgary's climate.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">5. What Exactly Is Included in Your Quote?</h2>
              <p className="mb-4">
                Before accepting a contractor's estimate, ask for a clear breakdown of what you are paying for. A Calgary deck quote should identify the labour, materials, demolition, disposal, foundation, framing, decking, stairs, railings, permits, engineering or drawings, cleanup, and applicable taxes.
              </p>
              <p className="mb-6">
                Do not assume that an item is included simply because it seems necessary for the project. Ask the contractor to identify anything excluded from the quoted price and explain how additional work will be priced.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why Calgary Homeowners Should Compare Scope, Not Just Price</h3>
              <p className="mb-4">
                Two quotes for a similar deck can look very different because the scope, materials, foundation system, permitting, or labour included may not be the same. A lower quote can appear attractive while leaving important work out of the price.
              </p>
              <p className="mb-6">
                When comparing estimates, make sure you are comparing the same scope of work, not just the final number. Our guide on <a href="https://busybeaverconstruction.com/blog/5" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/5')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">how much it costs to build a deck in Calgary</a> provides more context on the factors that can affect deck pricing.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A detailed quote gives you a clearer understanding of what you are buying and makes it easier to identify differences between contractors. It also reduces the chance of unexpected costs caused by items that were never included in the original scope.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">6. What Could Cause the Price to Increase After Work Starts?</h2>
              <p className="mb-6">
                Even with a detailed estimate, some problems may only become visible after demolition begins. On an existing deck, a contractor may uncover rot, hidden ledger or rim joist damage, or unexpected site conditions. Permit or engineering requirements can also affect the final cost. Material changes requested during construction can increase the price as well.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">The Important Question</h3>
              <p className="mb-2">Ask:</p>
              <p className="mb-4 italic">
                &quot;If you discover an unexpected problem, will you stop and get my written approval before doing additional work?&quot;
              </p>
              <p className="mb-4">
                A professional contractor should explain how unexpected work will be handled before construction starts. Ask whether additional work will require a written change order that identifies the work, cost, and reason for the change.
              </p>
              <p className="mb-6">
                Do not rely on a verbal agreement when the project cost is changing. Keep the documentation for your records and make sure you understand the revised price before approving the work.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-4">
                Clear change orders help prevent scope creep and surprise costs. They also give both the homeowner and contractor a shared record of what changed and why.
              </p>
              <p className="mb-6">
                For Calgary homeowners replacing an older deck, this is particularly important because existing structural conditions may not be visible until the old structure is removed.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">7. How Much Deposit Do You Require, and What Is the Payment Schedule?</h2>
              <p className="mb-4">
                Before signing a contract with a Calgary contractor, ask how much you need to pay upfront and when the remaining payments are due. A clear payment schedule should explain the deposit, progress payments, milestone payments, and final payment.
              </p>
              <p className="mb-6">
                Ask what happens to the payment schedule if the project is delayed. Payments tied to clear construction milestones can make it easier to understand what you are paying for at each stage.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Verify Before Paying</h3>
              <p className="mb-6">
                Before making a deposit, ask for the contractor's business information and verify their insurance and WCB coverage. If prepaid contracting rules apply to the business or project, ask about the required Prepaid Contracting Business Licence. You can also ask whether the contractor is bonded and what protections apply to your payment.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Builders' Lien Risk</h3>
              <p className="mb-6">
                Ask how the contractor handles payments to subcontractors and suppliers. Unpaid parties can create problems for homeowners, so it is worth understanding how these payments are managed.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A clear payment schedule helps you understand your financial commitment before construction begins. It also reduces uncertainty if the project takes longer than expected or additional work is proposed. Never agree to a payment arrangement you do not fully understand.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">8. Who Will Actually Build My Project?</h2>
              <p className="mb-4">
                Before hiring a Calgary contractor, ask who will be responsible for the work once the contract is signed. The person who provides your estimate may be the owner or estimator, while the actual construction may be handled by employees or subcontractors.
              </p>
              <p className="mb-4">Ask:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Who will supervise the construction crew?</li>
                <li>Will employees or subcontractors perform the work?</li>
                <li>Who is responsible for quality control?</li>
                <li>How often will the project be inspected?</li>
                <li>Who should I contact if I have a concern during construction?</li>
              </ul>
              <p className="mb-6">
                A contractor should be able to clearly explain who is responsible for managing the project and maintaining workmanship standards.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Red Flag</h3>
              <p className="mb-6">
                Be cautious if the person who sells the project becomes difficult to reach after you sign and an unfamiliar crew arrives without a clear supervisor or point of contact. The issue is not whether a contractor uses subcontractors. The important question is who is accountable for the finished work.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                Knowing who will actually build and supervise your project gives you a clearer line of accountability. It also helps you understand who to contact when questions or problems come up during construction. For a homeowner, the contractor's responsibility should remain clear from the first estimate through project completion.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">9. What Is Your Process for Keeping the Job on Schedule?</h2>
              <p className="mb-4">
                Before hiring a Calgary contractor, ask for a realistic start date, expected completion date, and explanation of how the project will be scheduled. A reliable contractor should also explain when materials will be ordered and how they handle common Calgary delays, including weather and permit approvals.
              </p>
              <p className="mb-6">
                Ask how many projects the crew is handling at the same time and what happens if your project falls behind. For outdoor construction in Calgary, weather can affect scheduling, so it is better to understand the contractor's process before work begins rather than assume the original date is guaranteed.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Ask Directly</h3>
              <p className="mb-2 italic">
                &quot;Will my project have a dedicated crew, or will the crew move between multiple jobs?&quot;
              </p>
              <p className="mb-4">
                If the contractor expects to move between projects, ask how that will affect your timeline and who will keep you updated.
              </p>
              <p className="mb-6">
                For more information about seasonal planning, see our guide to the <a href="https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/blog/best-time-to-build-deck-calgary')} className="text-[#0f172a] font-bold underline decoration-2 decoration-[#86efac]/50 hover:decoration-[#86efac] transition-colors">best time to build a deck in Calgary</a>.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A clear schedule gives you a realistic expectation of when work will start and finish. It also helps you understand how delays will be communicated and managed. The goal is not a contractor who promises the fastest completion, but one who gives you a realistic timeline and a clear plan for handling delays.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">10. How Will You Protect and Clean My Property?</h2>
              <p className="mb-4">
                Ask your Calgary contractor how they will protect your property before construction begins. This should include your lawn and landscaping, driveway, siding, doors, and windows. It is also worth asking how construction materials and debris will be managed during the project.
              </p>
              <p className="mb-6">
                A good contractor should have a clear cleanup process for nails, screws, metal shavings, and other debris. Ask whether the crew performs daily cleanup and whether a final site inspection is completed before the project is considered finished.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Calgary-Specific Concern</h3>
              <p className="mb-6">
                Outdoor construction often takes place close to neighbouring homes, driveways, lawns, and walkways. Loose nails, screws, and metal fragments can become a safety concern for homeowners, children, pets, and vehicles.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Green Flag</h3>
              <p className="mb-2">Ask:</p>
              <p className="mb-4 italic">
                &quot;Do you use a magnetic sweeper and complete a final cleanup inspection before leaving the site?&quot;
              </p>
              <p className="mb-6">
                A clear answer shows that the contractor has considered cleanup as part of the job, rather than treating it as an afterthought.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                Protecting your property and leaving the site clean are part of good project management. Clarifying these expectations before work begins helps you understand what the contractor will be responsible for throughout construction and at completion.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">11. Are You Properly Insured and Covered by WCB?</h2>
              <p className="mb-4">
                Before hiring a Calgary contractor, ask what insurance coverage they carry and whether they are covered by WCB-Alberta. At minimum, ask about general liability insurance and coverage for property damage that could occur during construction.
              </p>
              <p className="mb-4">
                Do not rely only on a verbal answer. Ask the contractor to provide current documentation, such as a certificate of insurance, and confirm that the information is current before work begins.
              </p>
              <p className="mb-4">You can also ask:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>What does your liability insurance cover?</li>
                <li>Is property damage covered?</li>
                <li>Do you have current WCB coverage?</li>
                <li>Can I see the relevant documentation?</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-4">
                Construction work involves people, equipment, materials, and your property. Confirming insurance and WCB coverage before the project starts gives you a clearer understanding of how risks are handled.
              </p>
              <p className="mb-6">
                For a homeowner in Calgary, Alberta, documentation is more useful than a verbal assurance. If a contractor is unwilling or unable to provide reasonable proof of their coverage, consider that a warning sign before signing the contract.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">12. Can You Provide References for Similar Calgary Projects?</h2>
              <p className="mb-4">
                Ask your Calgary contractor for references from homeowners with projects similar to yours in size, materials, and complexity. Recent projects can give you a better picture of their current workmanship and communication.
              </p>
              <p className="mb-4">When speaking with a reference, ask:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Was the project completed on time?</li>
                <li>Did the final price change?</li>
                <li>How were unexpected problems handled?</li>
                <li>Was communication clear?</li>
                <li>Was the property cleaned properly?</li>
                <li>Did the contractor respond to warranty concerns?</li>
              </ul>
              <p className="mb-6">
                For a deck project, ask to see similar completed work and, when possible, speak directly with the homeowner.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                Relevant references help you verify a contractor's recent experience, workmanship, reliability, and follow through before making your decision.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">13. What Warranty Do You Provide?</h2>
              <p className="mb-4">
                Ask your Calgary contractor what warranty covers the workmanship and what is covered separately by the manufacturer. Make sure you understand what each warranty includes, how long it lasts, and what is excluded.
              </p>
              <p className="mb-4">Ask:</p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>What does your workmanship warranty cover?</li>
                <li>What manufacturer warranties apply to the materials?</li>
                <li>What is excluded?</li>
                <li>How do I make a warranty claim?</li>
                <li>Who handles problems after final payment?</li>
              </ul>
              <p className="mb-6">
                Get the warranty terms in writing before construction begins. A clear warranty should explain who is responsible for addressing workmanship concerns and how you can contact the contractor if an issue appears after the project is complete.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A written warranty gives you a clear understanding of your protection after construction and who is responsible for resolving problems.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">14. Can I Review the Contract Before I Sign?</h2>
              <p className="mb-4">
                Before signing with a Calgary contractor, read the complete contract and make sure the details match your quote. The agreement should clearly state the project scope, materials, total price, payment schedule, and expected timeline.
              </p>
              <p className="mb-6">
                It should also explain who is responsible for permits, subcontractors, change orders, warranty work, cleanup, and other project responsibilities.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Check That Your Contract Covers</h3>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Scope of work</li>
                <li>Materials</li>
                <li>Price</li>
                <li>Payment schedule</li>
                <li>Timeline</li>
                <li>Permits</li>
                <li>Subcontractors</li>
                <li>Change orders</li>
                <li>Warranty</li>
                <li>Cleanup</li>
                <li>Responsibilities</li>
              </ul>
              <p className="mb-6">
                Do not sign until you understand what you are agreeing to and what happens if the project changes.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">15. What Are the Biggest Risks With My Project?</h2>
              <p className="mb-2">One of the most useful questions you can ask a Calgary contractor is:</p>
              <p className="mb-4 italic">
                &quot;If this were your home, what would you be most concerned about with this project?&quot;
              </p>
              <p className="mb-4">
                Pay attention to whether the contractor identifies risks before work begins. Depending on the project and property, these may include permits, frost, drainage, existing structural damage, materials, setbacks, access, scheduling, or budget.
              </p>
              <p className="mb-4">
                A contractor who takes time to assess the site should be able to explain the issues they see and how they plan to address them. They should also tell you when a specialist, drawing, engineering review, or permit may be needed.
              </p>
              <p className="mb-6">
                Be cautious if a contractor immediately says there are "no risks" without properly assessing your property.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                Good planning starts with understanding potential problems before construction begins. Asking this question can reveal whether the contractor has carefully considered your property, project requirements, and possible challenges.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Calgary Contractor Red Flags</h2>
              <p className="mb-4">
                Not every warning sign means a contractor is unreliable, but several red flags together should make you pause before signing. When comparing contractors in Calgary, watch for these issues:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6 marker:text-[#86efac]">
                <li>Says permits are unnecessary when your project requires them</li>
                <li>Cannot explain Calgary specific requirements</li>
                <li>Gives a quote without properly assessing the site</li>
                <li>Provides a vague lump sum quote</li>
                <li>Refuses to clearly itemize the scope of work</li>
                <li>Demands an unusually large upfront payment</li>
                <li>Cannot provide requested insurance or WCB documentation</li>
                <li>Avoids questions about insurance or WCB coverage</li>
                <li>Cannot clearly explain who will perform and supervise the work</li>
                <li>Has unclear arrangements with subcontractors</li>
                <li>Pressures you to sign the contract immediately</li>
                <li>Cannot explain how additional work and change orders are handled</li>
                <li>Has a history of leaving projects unfinished for extended periods</li>
                <li>Has no clear process for protecting or cleaning your property</li>
                <li>Cannot clearly explain the workmanship or manufacturer warranty</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Why This Matters</h3>
              <p className="mb-6">
                A single issue may have a reasonable explanation. However, multiple warning signs can indicate poor communication, unclear responsibilities, or avoidable project risk. Before hiring a Calgary contractor, make sure you are comfortable with the answers, documentation, contract, and payment terms rather than relying on price alone.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">How to Compare Three Calgary Contractor Quotes</h2>
              <p className="mb-6">
                Getting three quotes can help you understand the market, but comparing only the final price can be misleading. Make sure each Calgary contractor is quoting the same scope of work, materials, foundation, permits, and finishing details.
              </p>

              <div className="overflow-x-auto my-8 shadow-sm rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse bg-white text-base">
                  <thead>
                    <tr className="bg-[#0f172a] text-white">
                      <th className="p-4 font-bold font-secondary">Factor</th>
                      <th className="p-4 font-bold font-secondary">Contractor A</th>
                      <th className="p-4 font-bold font-secondary">Contractor B</th>
                      <th className="p-4 font-bold font-secondary">Contractor C</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Calgary experience</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Similar projects</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Permit included</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Foundation specified</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Materials specified</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Demolition included</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Railings and stairs included</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Cleanup included</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Insurance and WCB verified</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Payment schedule</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Warranty</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Timeline</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Change order process</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                    <tr className="bg-gray-50 hover:bg-gray-100 transition-colors">
                      <td className="p-4 text-gray-900 font-medium">Total price</td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                      <td className="p-4 text-gray-700"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">The Cheapest Quote Is Not Necessarily the Cheapest Project</h3>
              <p className="mb-4">
                A lower quote may leave out demolition, proper foundations, permits, engineering, cleanup, or specific materials. Those omissions can lead to additional costs once work begins.
              </p>
              <p className="mb-6">
                When comparing estimates, look at the complete scope and total expected cost, not just the number at the bottom of the quote. The best comparison is between contractors offering substantially the same work and specifications.
              </p>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Calgary Contractor Hiring Checklist</h2>
              <p className="mb-6">
                Use this checklist to compare contractors and confirm the important details before, during, and after your project.
              </p>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Before the Quote</h3>
              <ul className="list-none pl-0 space-y-2 mb-6">
                <li>☐ Contractor has relevant Calgary experience</li>
                <li>☐ Similar projects have been reviewed</li>
                <li>☐ Property and site conditions have been properly assessed</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Before Signing</h3>
              <ul className="list-none pl-0 space-y-2 mb-6">
                <li>☐ Permit requirements have been confirmed</li>
                <li>☐ Insurance and WCB have been verified</li>
                <li>☐ Detailed written quote has been received</li>
                <li>☐ Materials are clearly specified</li>
                <li>☐ Foundation system is specified</li>
                <li>☐ Payment schedule is understood</li>
                <li>☐ Change order process is understood</li>
                <li>☐ Warranty terms are documented</li>
                <li>☐ Contract has been reviewed</li>
              </ul>

              <h3 className="text-2xl font-bold text-[#0f172a] mt-8 mb-3 font-secondary tracking-tight">Before Final Payment</h3>
              <ul className="list-none pl-0 space-y-2 mb-6">
                <li>☐ Agreed work has been completed</li>
                <li>☐ Required inspections have been completed</li>
                <li>☐ Site has been properly cleaned</li>
                <li>☐ Warranty information has been received</li>
                <li>☐ Final project documentation has been received</li>
              </ul>

              <h2 className="text-3xl font-bold text-[#0f172a] mt-12 mb-6 font-secondary border-b border-gray-200 pb-3 tracking-tight">Frequently Asked Questions</h2>
              
              <div className="space-y-4 my-8">
                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">What should I ask a contractor before hiring them?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Ask about their Calgary experience, insurance and WCB coverage, permit requirements, project scope, materials, timeline, payment schedule, warranty, and references. The goal is to understand who is responsible for the work, what is included in the price, and how potential problems will be handled.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How do I choose a good contractor in Calgary?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Look beyond price. Compare relevant Calgary experience, documentation, scope clarity, communication, workmanship, project supervision, and accountability. A contractor should be able to explain how they will approach your specific property and project.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Does a Calgary deck require a permit?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    A deck more than 0.6 metres (2 feet) above grade requires a building permit in Calgary. Other project details can affect the requirements, so confirm the applicable permits with the City of Calgary for your specific project. For more information, see our guide to whether you need a permit to build a deck in Calgary.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">Should the contractor handle the building permit?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Ask the contractor exactly what they will handle, including drawings, permit applications, and inspections. Even when the contractor manages the process, homeowners should understand which permits their project requires and what is included in the contract.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">How many contractor quotes should I get?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Getting multiple quotes can help you understand the market, but there is no useful number if the quotes are not comparable. Focus on contractors providing an equivalent scope of work, including materials, foundation, permits, labour, cleanup, and warranty.
                  </div>
                </details>

                <details className="group bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden cursor-pointer font-text">
                  <summary className="px-6 py-5 list-none flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors">
                    <h3 className="font-bold text-gray-800 text-lg m-0">What are the biggest contractor red flags?</h3>
                    <span className="text-[#86efac] group-open:rotate-180 transition-transform duration-300 ml-4 flex-shrink-0">
                      ▼
                    </span>
                  </summary>
                  <div className="px-6 py-5 text-gray-600 border-t border-gray-100 leading-relaxed">
                    Watch for contractors who dismiss required permits, cannot explain Calgary requirements, provide vague quotes, demand large upfront payments, avoid insurance or WCB questions, have unclear supervision, pressure you to sign, or cannot explain change orders and warranty coverage.
                  </div>
                </details>
              </div>

              {/* Call to Action Section */}
              <div className="mt-16 mb-8 border-t border-gray-200 pt-12 bg-gray-900 rounded-xl p-8 shadow-xl border-l-4 border-[#86efac]">
                <h2 className="text-2xl font-bold mb-4 font-secondary text-[#86efac]">What Matters Most When Choosing a Calgary Contractor</h2>
                <p className="mb-4 text-gray-300">
                  Choosing the right contractor is about more than comparing prices. You need a team that understands your project, provides a clear scope, uses appropriate materials, and can manage the work from start to finish.
                </p>
                <p className="mb-6 text-gray-300">
                  If you are planning a new deck, replacing an existing deck, or improving your outdoor space, Busy Beaver Construction can help. Explore our <a href="https://busybeaverconstruction.com" onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com')} className="text-[#86efac] font-bold hover:underline">deck building services in Calgary</a> or request an estimate to discuss your project with our team.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="https://busybeaverconstruction.com/estimate" 
                    onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com/estimate')}
                    className="inline-flex justify-center items-center px-6 py-3 bg-[#86efac] text-black font-bold rounded shadow-lg hover:brightness-110 transition-all duration-300 font-accent tracking-wide"
                  >
                    Request an Estimate
                  </a>
                  <a 
                    href="https://busybeaverconstruction.com" 
                    onClick={(e) => handleNavigation(e, 'https://busybeaverconstruction.com')}
                    className="inline-flex justify-center items-center px-6 py-3 bg-white text-black font-bold rounded shadow-lg hover:bg-gray-100 transition-all duration-300 font-accent tracking-wide"
                  >
                    Explore Deck Services
                  </a>
                </div>
              </div>

            </article>

            {/* Sidebar Inspiration Gallery */}
            <aside className="lg:col-span-4 lg:sticky lg:top-24 h-fit mt-8 lg:mt-6">
              <div className="bg-white rounded shadow-sm border border-gray-100 pb-6">
                <h3 className="text-[22px] font-bold mb-4 font-secondary text-[#0f172a] px-6 pt-6 tracking-tight">Project Inspiration</h3>
                
                <div className="grid grid-cols-1 gap-5 max-h-[850px] overflow-y-auto px-6 custom-scrollbar">
                  {[
                    "/assets/images/Decks/Wood Decks/Simple-ground-level-garden-deck.jpg",
                    "/assets/images/Decks/Composite Decks/Outdoor-hot-tub-area-with-covered-lounging-space.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-Terrain-multi-level-deck-in-Brown-Oak-trim-and-Silver-Maple-surface.-LED-strip-lighting-on-stairs.jpg",
                    "/assets/images/Decks/Composite Decks/Timbertech-deck-with-PT-wood-skirting-for-underdeck-storage.jpg",
                    "/assets/images/Decks/Wood Decks/Multi-level-Cedar-wood-deck.jpg"
                  ].map((src, index) => (
                    <div key={index} className="overflow-hidden h-[220px] group cursor-pointer shadow-sm border-2 border-white ring-1 ring-gray-100">
                      <img 
                        src={src}
                        alt={`Inspiration image ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 loading-lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </aside>
            
          </div>
        </div>
      </section>

      <Footer />
    </main>
  )
}